/**
 * Where the garden being tended is and what day it is there, as the server
 * decides them (bloom-backend routes/gardenPlace; architecture audit batch 5,
 * A29 and A31): its postcode, whether Grow can plan from it, why a saved one
 * cannot be used, whether the person asking owns it, and the garden's own
 * day from its time zone (never the phone's), and for a member the owner's
 * share identity (ownerShare: the owner's handles and garden address, which a
 * member's share pictures print in place of their own; B37).
 *
 * In a shared garden it is the owner's postcode and the owner's day: the
 * request carries X-Grow-Garden (apiClient), and the server reads the
 * garden at its owner's place. The app sends nothing else.
 *
 * Endpoint: GET /garden-place → GardenPlace (its `success` included).
 * Who uses it: shared/hooks/useGardenPostcode, which keeps it under
 * gardenPlaceKey and reads it again at the garden's next midnight.
 * Auth: the signed-in token.
 *
 * @module api/gardenPlace
 */

import { apiClient } from './apiClient';
import { GARDEN_PLACE_QUERY_KEY } from './gardenPlaceKey';
import type { ShownLink } from '@/shared/lib/socialLinks';

/** The garden's place and day, as the server says them. */
export interface GardenPlace {
  /** The server's success flag, passed through. */
  success: boolean;
  /** The garden's postcode as saved ('' for none): the owner's in a shared garden. */
  zip: string;
  /** Whether Grow can plan from it: the tabs show, and Today asks for nothing. */
  usable: boolean;
  /** Why the saved one cannot be used (FORMAT, NOT_FOUND, NO_FORECAST), or null when it can or there is none. */
  reason: string | null;
  /** The server's English sentence for that reason, or null. */
  message: string | null;
  /** Whether the person asking owns the garden (false for a member, who cannot give it a postcode). */
  own: boolean;
  /** The place's town, when usable. */
  city: string | null;
  /** The place's state, when usable. */
  state: string | null;
  /** The garden's own day, "YYYY-MM-DD", from its time zone. */
  date: string;
  /** The garden's time zone, e.g. "America/Chicago". */
  timeZone: string;
  /** The garden's next midnight, an ISO stamp: the place is read again after it. */
  validUntil: string;
  /**
   * For a member tending someone else's garden, the owner's share identity: the owner's links as the server cleans
   * them (key, display, url) and garden address (slug, null before they have one). Null in the person's own garden. Always sent (C118 F3).
   */
  ownerShare: OwnerShare | null;
  /**
   * B64: the owner's garden has had a plant of its own, ever (archived ones count; the sign-up example plant does not).
   * False is a new garden, which gets the two start doors (B96). Always sent (C118 F3: no fallback for a server without it).
   */
  started: boolean;
}

/** A garden owner's share identity, as the server sends it to a member (B37). */
export interface OwnerShare {
  /** The owner's links that pass the platforms' rules, each with its printed handle and address (E7), in order. */
  links: ShownLink[];
  /** The owner's garden address (/@slug), or null before they have one. */
  slug: string | null;
}

/**
 * The key the garden's place is kept under: the garden being tended and the
 * signed-in person's own saved postcode, so saving one reads it again.
 *
 * @param gardenId - The garden being tended (the chosen one, else the person's own), or null before sign-in.
 * @param ownZip - The signed-in person's own saved postcode, as their account has it.
 */
export function gardenPlaceKey(gardenId: number | null, ownZip: string): [string, number | null, string] {
  return [GARDEN_PLACE_QUERY_KEY, gardenId, ownZip.trim()];
}

/**
 * Reads where the garden being tended is, and its day.
 *
 * @returns The server's answer.
 */
export async function getGardenPlace(): Promise<GardenPlace> {
  const response = await apiClient.get<GardenPlace>('/garden-place');
  return response.data;
}
